import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2, Circle, X, Sparkles, Trophy } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { hasReadyBadge, grantReadyBadge } from '@/hooks/useCoachTour';
import type { Team } from '@/hooks/useCoach';

const ONBOARDING_KEY = 'sportara-coach-onboarding-dismissed';

function readDismissed(): boolean {
  try {
    return localStorage.getItem(ONBOARDING_KEY) === 'true';
  } catch {
    return false;
  }
}

function writeDismissed() {
  try {
    localStorage.setItem(ONBOARDING_KEY, 'true');
  } catch {
    // ignore
  }
}

interface CoachOnboardingChecklistProps {
  teams: Team[];
  coachId?: string;
}

export default function CoachOnboardingChecklist({ teams, coachId }: CoachOnboardingChecklistProps) {
  const { user } = useAuth();
  const [dismissed, setDismissed] = useState(() => readDismissed());
  const [hasMembers, setHasMembers] = useState(false);
  const [hasContent, setHasContent] = useState(false);
  const [hasCompletion, setHasCompletion] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [badgeEarned, setBadgeEarned] = useState(false);

  const fetchStatus = useCallback(async () => {
    if (!user) return;
    if (teams.length === 0) { setLoaded(true); return; }
    const teamIds = teams.map(t => t.id);
    const [membersRes, assignmentsRes, contentRes, ...completionRes] = await Promise.all([
      supabase.from('team_members').select('id').in('team_id', teamIds).limit(1),
      supabase.from('lesson_assignments').select('id').in('team_id', teamIds).limit(1),
      coachId
        ? (supabase.from('coach_content' as any) as any).select('id').eq('coach_id', coachId).limit(1)
        : Promise.resolve({ data: [] }),
      ...teamIds.map(id => supabase.rpc('get_team_assignment_completion' as any, { p_team_id: id })),
    ]);
    setHasMembers(!!membersRes.data?.length);
    setHasContent(!!assignmentsRes.data?.length || !!contentRes.data?.length);
    setHasCompletion(completionRes.some((r: any) => (r?.data?.completed_assignment_instances ?? 0) > 0));
    setLoaded(true);
  }, [user, teams, coachId]);

  // Re-derive from the database whenever assignments or progress change.
  useEffect(() => {
    fetchStatus();
    const h = () => fetchStatus();
    window.addEventListener('assignment-completion-changed', h);
    window.addEventListener('focus', h);
    return () => {
      window.removeEventListener('assignment-completion-changed', h);
      window.removeEventListener('focus', h);
    };
  }, [fetchStatus]);

  const items = [
    { id: 'profile', label: 'Coach profile created', done: true },
    { id: 'team', label: 'Team created', done: teams.length > 0 },
    { id: 'content', label: 'First lesson or content assigned', done: hasContent },
    { id: 'player', label: 'First player joined with your team code', done: hasMembers },
    { id: 'completion', label: 'First assignment completed by a player', done: hasCompletion },
  ];

  const completedCount = items.filter(i => i.done).length;
  const allDone = completedCount === items.length;

  // Award the badge once, then retire the checklist.
  useEffect(() => {
    if (!loaded || !allDone || dismissed) return;
    if (!hasReadyBadge()) {
      grantReadyBadge();
      setBadgeEarned(true);
      const t = window.setTimeout(() => { writeDismissed(); setDismissed(true); }, 4000);
      return () => window.clearTimeout(t);
    }
    writeDismissed();
    setDismissed(true);
  }, [loaded, allDone, dismissed]);

  if (dismissed || !loaded) return null;

  const handleDismiss = () => { writeDismissed(); setDismissed(true); };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -10 }}
        transition={{ duration: 0.3 }}
      >
        <Card className="p-4 border-primary/30 bg-primary/5 overflow-hidden">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-primary" />
              <h3 className="font-display font-bold text-sm">Getting Started</h3>
              <span className="text-[10px] font-display font-bold text-muted-foreground">
                {completedCount}/{items.length}
              </span>
            </div>
            <button
              onClick={handleDismiss}
              className="text-muted-foreground hover:text-foreground transition-colors"
              aria-label="Dismiss checklist"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className="w-full bg-muted rounded-full h-1.5 mb-4">
            <motion.div
              className="bg-primary rounded-full h-1.5"
              initial={{ width: 0 }}
              animate={{ width: `${(completedCount / items.length) * 100}%` }}
              transition={{ duration: 0.5 }}
            />
          </div>

          <div className="space-y-1.5">
            {items.map((item, idx) => (
              <motion.div
                key={item.id}
                initial={{ opacity: 0, x: -8 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: idx * 0.05 }}
                className={`flex items-center gap-2.5 p-2 rounded-lg ${item.done ? 'bg-primary/10' : 'bg-background/60'}`}
              >
                <div className={`shrink-0 ${item.done ? 'text-primary' : 'text-muted-foreground'}`}>
                  {item.done ? <CheckCircle2 className="w-4 h-4" /> : <Circle className="w-4 h-4" />}
                </div>
                <p className={`text-xs font-display font-bold ${item.done ? 'text-foreground' : 'text-muted-foreground'}`}>
                  {item.label}
                </p>
              </motion.div>
            ))}
          </div>

          {badgeEarned && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="mt-3 flex items-center justify-center gap-2 rounded-lg bg-primary/15 ring-1 ring-primary/40 py-2"
            >
              <Trophy className="w-4 h-4 text-primary" />
              <span className="text-xs font-display font-bold text-primary">Badge earned: Ready to Coach</span>
            </motion.div>
          )}
        </Card>
      </motion.div>
    </AnimatePresence>
  );
}
